import { useCallback, MouseEvent } from 'react';

import {
  flexRender,
  Table,
  Header,
  HeaderGroup,
  Row,
  Cell,
  RowData,
} from '@tanstack/react-table';

import {
  getDisableHighlightOnMouseDown,
  getMultiRowRangeSelection,
} from './getMultiRowRangeSelection';
import { DEFAULT_TABLE_SIZE } from '../consts';
import { Themeable } from '../types';

type UseTableArgs<TData extends RowData> = Themeable & {
  table: Table<TData>;
  enableMultiRowRangeSelection?: boolean;
  enableFullRowClick?: boolean;
};

const useTable = <TData extends RowData, TValue = unknown>({
  table,
  tableSize = DEFAULT_TABLE_SIZE,
  enableMultiRowRangeSelection = false,
  enableFullRowClick = true,
}: UseTableArgs<TData>) => {
  const {
    columnSizingInfo,
    columnOrder,
    columnVisibility,
    columnSizing,
    columnPinning,
  } = table.getState();
  const columnDefs = table.options.columns;
  const selectedRows = table.getSelectedRowModel().flatRows;
  const allRows = table.getRowModel().rows;
  const { setRowSelection } = table;

  const getTableHeaderGroupProps = useCallback(
    (_headerGroup: HeaderGroup<TData>) => ({
      tableSize,
    }),
    [tableSize],
  );

  const getTableHeaderProps = useCallback(
    (
      header: Header<TData, TValue>,
      headerType: 'header' | 'footer' = 'header',
    ) => ({
      tableSize,
      colSpan: header.colSpan,
      size: header.getSize(),
      isPlaceholder: header.isPlaceholder,
      hasSubHeaders: header.subHeaders.length > 0,
      canResize: header.column.getCanResize(),
      isSorted: header.column.getIsSorted(),
      canSort: header.column.getCanSort(),
      toggleSort: header.column.getToggleSortingHandler(),
      isResizingAnyColumn: !!columnSizingInfo.isResizingColumn,
      isResizingColumn: header.column.getIsResizing(),
      handleResize: header.getResizeHandler(),
      renderValue: () =>
        flexRender(header.column.columnDef[headerType], {
          tableSize,
          ...header.getContext(),
        }),
    }),
    [tableSize, columnSizingInfo],
  );

  const getTableRowProps = useCallback(
    (row: Row<TData>) => ({
      tableSize,
      canSelect: enableFullRowClick && row.getCanSelect(),
      toggleSelected: (val?: boolean, e?: MouseEvent) => {
        const selectValue = val === undefined ? !row.getIsSelected() : val;
        if (
          enableMultiRowRangeSelection &&
          e?.shiftKey &&
          selectedRows.length &&
          selectValue
        ) {
          setRowSelection((old) => ({
            ...old,
            ...getMultiRowRangeSelection(row, selectedRows, allRows),
          }));
        } else {
          row.toggleSelected?.(val);
        }
      },
      isSelected: row.getIsSelected(),
      isHighlightable: row.getCanSelect(),
      onMouseDown: getDisableHighlightOnMouseDown({
        enableMultiRowRangeSelection,
        selectedRows,
      }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- column state is not read in the body but must recreate row props when columns reorder/hide/resize/pin
    [
      tableSize,
      selectedRows,
      allRows,
      setRowSelection,
      enableMultiRowRangeSelection,
      enableFullRowClick,
      columnOrder,
      columnVisibility,
      columnSizing,
      columnPinning,
    ],
  );

  const getTableCellProps = useCallback(
    (cell: Cell<TData, TValue>) => ({
      tableSize,
      size: cell.column.getSize(),
      isResizing: cell.column.getIsResizing(),
      enableRowSelection: enableFullRowClick && cell.row.getCanSelect(),
      children: flexRender(cell.column.columnDef.cell, {
        tableSize,
        ...cell.getContext(),
      }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- exhaustive-deps can't trace columnDefs through cell.column.columnDef; column state deps must recreate cell props when columns change so flexRender output stays current
    [
      tableSize,
      enableFullRowClick,
      columnOrder,
      columnVisibility,
      columnSizing,
      columnPinning,
      columnDefs,
    ],
  );

  return {
    getTableHeaderGroupProps,
    getTableHeaderProps,
    getTableRowProps,
    getTableCellProps,
  };
};
export default useTable;
